import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'

const HOT_CACHE_ERROR_EVENT = 'hot_cache.error'
const MAX_CACHE_SIZE = 250

type HotCacheErrorType = 'get_item' | 'set_item' | 'remove_item'

let sendAnalyticsEventFn: SendAnalyticsEventFunction | undefined

// LRU cache implementation using Map (maintains insertion order)
class LRUCache {
  #cache: Map<string, unknown>
  #maxSize: number

  constructor(maxSize: number) {
    this.#cache = new Map()
    this.#maxSize = maxSize
  }

  get(key: string): unknown | undefined {
    const value = this.#cache.get(key)
    if (value !== undefined) {
      // Move to end (most recently used)
      this.#cache.delete(key)
      this.#cache.set(key, value)
    }
    return value
  }

  set(key: string, value: unknown): void {
    // If key exists, delete it first to update position
    if (this.#cache.has(key)) {
      this.#cache.delete(key)
    }
    // Add to end (most recently used)
    this.#cache.set(key, value)

    // Evict least recently used if over capacity
    if (this.#cache.size > this.#maxSize) {
      const firstKey = this.#cache.keys().next().value
      if (firstKey !== undefined) {
        this.#cache.delete(firstKey)
      }
    }
  }

  delete(key: string): void {
    this.#cache.delete(key)
  }

  clear(): void {
    this.#cache.clear()
  }

  get size(): number {
    return this.#cache.size
  }
}

const lruCache = new LRUCache(MAX_CACHE_SIZE)

function reportError(errorType: HotCacheErrorType, key: string, error: unknown) {
  if (sendAnalyticsEventFn) {
    sendAnalyticsEventFn(HOT_CACHE_ERROR_EVENT, key, {
      error_type: errorType,
      error_message: error instanceof Error ? error.message : 'Unknown error',
    })
  }
}

export const InMemoryLRUQueryCache = {
  init(sendAnalyticsEvent: SendAnalyticsEventFunction): void {
    sendAnalyticsEventFn = sendAnalyticsEvent
  },

  getItem<T>(key: string): T | undefined {
    try {
      return lruCache.get(key) as T | undefined
    } catch (error) {
      reportError('get_item', key, error)
      return undefined
    }
  },

  setItem<T>(key: string, value: T): void {
    try {
      lruCache.set(key, value)
    } catch (error) {
      reportError('set_item', key, error)
    }
  },

  removeItem(key: string): void {
    try {
      lruCache.delete(key)
    } catch (error) {
      reportError('remove_item', key, error)
    }
  },

  // Exposed for testing
  clear(): void {
    lruCache.clear()
  },

  // Exposed for testing
  size(): number {
    return lruCache.size
  },
}
